// @ts-check
import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';

import {
  countries,
  states,
  tradeshows,
  sessionTypes,
  roleTypes,
} from 'services/inputTokens';
import { getFilter } from 'selectors/organisation';
import { setFilterGroup } from 'actions/organisation';
import { Tooltip } from 'components/common';
import {
  tradeshowOrganisationKeys,
  tradeshowResearcherKeys,
} from 'components/Search/Filters/keys';
import { helpText } from 'components/Search/helpers';
import TradeshowFlagsWarning from 'components/Search/Filters/TradeshowFlagsWarning';
import {
  ATTENDING_PRESENTER_ROLES,
  isMissingTradeshowFlag,
} from 'reducers/tradeshowFilterRules';
import { getResearcherText } from 'utils/constants';
import { tagRender } from 'components/common/TagRender';
import { renderOption } from 'components/common/OptionRender';

import OrgFilterField from './OrgFilterField';
import OrgFilterSection from './OrgFilterSection';

// SCIL-17770: Tradeshows section for the org People/Activity panels. Mirrors
// the main-search Tradeshows section but binds to the shared org `filters`
// slice. The Exhibiting-at / Presenting-at checkboxes redirect the queried
// index field inside tradeshowFormat (utils/search.js).

const activeKeys = [
  ...Object.keys(tradeshowResearcherKeys),
  ...Object.keys(tradeshowOrganisationKeys),
];

const OrgTradeshowsSection = ({ organisationId, loading }) => {
  const dispatch = useDispatch();
  const tradeshowValue = useSelector(
    getFilter(organisationId, tradeshowResearcherKeys.tradeshow.key)
  );
  const sessionTypeValue = useSelector(
    getFilter(organisationId, tradeshowResearcherKeys.sessionType.key)
  );
  const exhibitingSelected = useSelector(
    getFilter(organisationId, tradeshowOrganisationKeys.exhibitingShow.key)
  );
  const presentingSelected = useSelector(
    getFilter(
      organisationId,
      tradeshowOrganisationKeys.presentingAtTradeshow.key
    )
  );
  const thisShowOnlySelected = useSelector(
    getFilter(organisationId, tradeshowResearcherKeys.thisShowOnly.key)
  );
  const roleTypeValue = useSelector(
    getFilter(organisationId, tradeshowResearcherKeys.roleType.key)
  );
  const thisRoleTypeOnlyValue = useSelector(
    getFilter(organisationId, tradeshowResearcherKeys.thisRoleTypeOnly.key)
  );
  const attendingPresentersOnlyValue = useSelector(
    getFilter(
      organisationId,
      tradeshowResearcherKeys.attendingPresentersOnly.key
    )
  );

  const hasTradeshow = tradeshowValue?.length > 0;
  const showFlagsWarning = isMissingTradeshowFlag({
    tradeshow: tradeshowValue,
    exhibitingShow: exhibitingSelected,
    presentingAtTradeshow: presentingSelected,
    thisShowOnly: thisShowOnlySelected,
  });

  const checkboxLabel =
    tradeshowValue?.length > 1
      ? 'Search selected tradeshows only'
      : 'Search selected tradeshow only';

  const sessionTypeCheckboxLabel =
    sessionTypeValue?.length > 1
      ? 'Only show results with selected session types'
      : 'Only show results with selected session type';

  const roleTypeCheckboxLabel =
    roleTypeValue?.length > 1
      ? 'Only show results with selected roles'
      : 'Only show results with selected role';

  const disableThisTradeshowOnly = exhibitingSelected && presentingSelected;

  const setOrgFilter = (id, value) =>
    dispatch(setFilterGroup(organisationId, id, value));

  // Roles go first so the shared reducer rule sees the first pick; the explicit
  // thisRoleTypeOnly then covers a role list the user had already set to broad.
  const handlePresenters = value => {
    if (value) {
      setOrgFilter(tradeshowResearcherKeys.roleType.key, [
        ...ATTENDING_PRESENTER_ROLES,
      ]);
      setOrgFilter(tradeshowResearcherKeys.thisRoleTypeOnly.key, true);
      setOrgFilter(tradeshowResearcherKeys.attendingPresentersOnly.key, true);
    } else {
      setOrgFilter(tradeshowResearcherKeys.attendingPresentersOnly.key, false);
      setOrgFilter(tradeshowResearcherKeys.roleType.key, []);
    }
  };

  // Same switch sync as main search's Tradeshows: on only while every presenter
  // role is picked and limited to the selected roles.
  useEffect(() => {
    const key = tradeshowResearcherKeys.attendingPresentersOnly.key;
    const isPresenterSelection =
      Boolean(roleTypeValue?.length) &&
      Boolean(thisRoleTypeOnlyValue) &&
      ATTENDING_PRESENTER_ROLES.every(role => roleTypeValue.includes(role));

    if (attendingPresentersOnlyValue && !isPresenterSelection) {
      dispatch(setFilterGroup(organisationId, key, false));
    } else if (!attendingPresentersOnlyValue && isPresenterSelection) {
      dispatch(setFilterGroup(organisationId, key, true));
    }
  }, [
    roleTypeValue,
    thisRoleTypeOnlyValue,
    attendingPresentersOnlyValue,
    organisationId,
    dispatch,
  ]);

  return (
    <OrgFilterSection
      organisationId={organisationId}
      icon='image'
      title={showFlagsWarning ? <TradeshowFlagsWarning /> : 'Tradeshows'}
      activeKeys={activeKeys}
    >
      <OrgFilterField
        filterType='selectIncludeExclude'
        placeholder='Tradeshow Names'
        mode={'multiple'}
        style={{ width: '100%' }}
        tagRender={tagRender}
        filterId={tradeshowResearcherKeys.tradeshow.key}
        organisationId={organisationId}
        setFilterGroup={setFilterGroup}
        getFilter={getFilter}
        options={tradeshows}
        disabledKey='dataNotAvailable'
        disabledTooltipText='Data not available yet'
        helpText={`Filter results by tradeshows that ${getResearcherText({
          isPlural: true,
        })} have attended / will be attending`}
        disabled={loading}
        optionProp={{ optionLabel: 'label', optionValue: 'label' }}
      />
      <OrgFilterField
        filterType='checkbox'
        filterId={tradeshowOrganisationKeys.exhibitingShow.key}
        organisationId={organisationId}
        setFilterGroup={setFilterGroup}
        getFilter={getFilter}
        helpText={helpText.exhibitingShow}
        disabled={!hasTradeshow || thisShowOnlySelected || loading}
        labelText={tradeshowOrganisationKeys.exhibitingShow.description}
      />
      <OrgFilterField
        filterType='checkbox'
        filterId={tradeshowOrganisationKeys.presentingAtTradeshow.key}
        organisationId={organisationId}
        setFilterGroup={setFilterGroup}
        getFilter={getFilter}
        helpText={helpText.presentingShow}
        disabled={!hasTradeshow || thisShowOnlySelected || loading}
        labelText={tradeshowOrganisationKeys.presentingAtTradeshow.description}
      />
      <OrgFilterField
        filterType='checkbox'
        filterId={tradeshowResearcherKeys.thisShowOnly.key}
        organisationId={organisationId}
        setFilterGroup={setFilterGroup}
        getFilter={getFilter}
        helpText={helpText.thisShowOnly}
        disabled={
          !tradeshowValue ||
          tradeshowValue.length === 0 ||
          disableThisTradeshowOnly ||
          loading
        }
        labelText={checkboxLabel}
      />
      <OrgFilterField
        filterType='selectIncludeExclude'
        mode={'multiple'}
        style={{ width: '100%' }}
        renderOption={renderOption}
        tagRender={tagRender}
        placeholder='Tradeshow Session Type'
        filterId={tradeshowResearcherKeys.sessionType.key}
        organisationId={organisationId}
        setFilterGroup={setFilterGroup}
        getFilter={getFilter}
        helpText={`Filter results by session types that ${getResearcherText({
          isPlural: true,
        })} have given`}
        options={sessionTypes}
        disabled={loading}
        remoteSearchOnClick
      />
      <OrgFilterField
        filterType='checkbox'
        filterId={tradeshowResearcherKeys.thisSessionTypeOnly.key}
        organisationId={organisationId}
        setFilterGroup={setFilterGroup}
        getFilter={getFilter}
        helpText={helpText.thisSessionTypeOnly}
        disabled={!sessionTypeValue || sessionTypeValue.length === 0 || loading}
        labelText={sessionTypeCheckboxLabel}
      />
      <OrgFilterField
        filterType='selectIncludeExclude'
        mode={'multiple'}
        style={{ width: '100%' }}
        renderOption={renderOption}
        tagRender={tagRender}
        placeholder='Tradeshow Role'
        filterId={tradeshowResearcherKeys.roleType.key}
        organisationId={organisationId}
        setFilterGroup={setFilterGroup}
        getFilter={getFilter}
        helpText={`Filter results by Tradeshow Roles that ${getResearcherText({
          isPlural: true,
        })} have performed`}
        options={roleTypes}
        disabled={loading}
        remoteSearchOnClick
      />
      <OrgFilterField
        filterType='checkbox'
        filterId={tradeshowResearcherKeys.thisRoleTypeOnly.key}
        organisationId={organisationId}
        setFilterGroup={setFilterGroup}
        getFilter={getFilter}
        helpText={helpText.thisRoleTypeOnly}
        disabled={!roleTypeValue || roleTypeValue.length === 0 || loading}
        labelText={roleTypeCheckboxLabel}
      />
      <Tooltip
        title={helpText.attendingPresentersOnly}
        placement='right'
        overlayStyle={{ maxWidth: 450 }}
      >
        <div>
          <OrgFilterField
            filterType='switch'
            filterId={tradeshowResearcherKeys.attendingPresentersOnly.key}
            organisationId={organisationId}
            setFilterGroup={setFilterGroup}
            getFilter={getFilter}
            onChange={handlePresenters}
            disabled={loading}
          >
            {tradeshowResearcherKeys.attendingPresentersOnly.description}
          </OrgFilterField>
        </div>
      </Tooltip>
      <div style={{ width: 'fit-content', cursor: 'default' }}>
        Tradeshow Location
      </div>
      <OrgFilterField
        filterType='selectIncludeExclude'
        mode={'multiple'}
        style={{ width: '100%' }}
        renderOption={renderOption}
        tagRender={tagRender}
        filterId={tradeshowResearcherKeys.tradeshowCountryRegion.key}
        organisationId={organisationId}
        setFilterGroup={setFilterGroup}
        getFilter={getFilter}
        helpText={helpText.filterByTradeshowCountryRegion}
        placeholder={tradeshowResearcherKeys.tradeshowCountryRegion.description}
        options={countries}
        disabled={loading}
      />
      <OrgFilterField
        filterType='selectIncludeExclude'
        mode={'multiple'}
        style={{ width: '100%' }}
        tagRender={tagRender}
        placeholder={tradeshowResearcherKeys.tradeshowState.description}
        filterId={tradeshowResearcherKeys.tradeshowState.key}
        organisationId={organisationId}
        setFilterGroup={setFilterGroup}
        getFilter={getFilter}
        helpText={helpText.filterByTradeshowState}
        options={states}
        remoteSearchOnClick
        optionProp={{ optionLabel: 'displayName', optionValue: 'id' }}
        disabled={loading}
      />
      <OrgFilterField
        filterType='token'
        placeholder={tradeshowResearcherKeys.tradeshowCity.description}
        filterId={tradeshowResearcherKeys.tradeshowCity.key}
        organisationId={organisationId}
        setFilterGroup={setFilterGroup}
        getFilter={getFilter}
        helpText={helpText.filterByTradeshowCity}
        disabled={loading}
        includeExclude
      />
    </OrgFilterSection>
  );
};

OrgTradeshowsSection.propTypes = {
  organisationId: PropTypes.number.isRequired,
  loading: PropTypes.bool,
};

export default OrgTradeshowsSection;
